import { CSSResultGroup, TemplateResult, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-teaser-card.css.lit';
import headingStyle from '../tufts-heading/tufts-heading.css.lit';
import '../tufts-heading/tufts-heading';
import '../tufts-icon/tufts-icon';
import { tuftsCard } from '../tufts-card/tufts-card';

/**
 * The TuftsTeaserCard component
 * @element tufts-teaser-card
 *
 * @slot heading
 * @slot media
 * @slot alternative-content
 * @slot eyebrow
 * @slot date
 * @slot description
 *
 * @attr url
 * @attr theme
 * @attr background-color
 * @attr external
 */
@customElement('tufts-teaser-card')
export class TuftsTeaserCard extends tuftsCard {
  static styles: CSSResultGroup = [componentStyles, headingStyle];

  /**
   * URL for card
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'url',
  })
  url: string;

  /**
   * Theme for card
   **/
  @property({
    type: String,
    reflect: true,
    attribute: 'theme',
  })
  theme = '';

  /**
   * Is link external (open in new tab)
   **/
  @property({
    type: Boolean,
    reflect: true,
    attribute: 'external',
  })
  external = false;

  /**
   * Optional max-size
   **/
  @property({
    type: Number,
    reflect: true,
    attribute: 'max-size',
  })
  maxSize: number;

  firstUpdated(): void {
    this.checkParentNodeContainer();
  }

  render(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;

    const classes = {
      'card-wrapper': true,
      'teaser-card': true,
      [`${this.theme}-theme`]: !!this.theme,
      'mobile': this.isMobile,
    };

    return html`
      <style>
        ${this.maxSize ? `:host { max-width: ${this.maxSize}px; }` : ''}
          .content-wrapper {
          background-color: var(--bg-color--${this.background});
        }
      </style>
      <article class="${classMap(classes)}">
        <div
          slot="primary-content"
          class="media-content ${this.isMobile
            ? 'mobile'
            : ''} ${!this.ngSlot.exist('media') ? 'placeholder-image' : ''}"
        >
          ${this.ngSlot.conditionalNGSlot('media')}
        </div>
        <div
          slot="secondary-content"
          class="content-wrapper ${this.setColorTemplate()} ${this.isMobile
            ? 'mobile'
            : ''} ${this.ngSlot.exist('media') ? null : 'no-media'}"
        >
          ${this.ngSlot.conditionalNGSlot('alternative-content')}
          ${this.ngSlot.conditionalNGSlot('eyebrow', true, 'xs')}
          ${this.headingTemplate()} ${this.ngSlot.conditionalNGSlot('date')}
          ${this.ngSlot.conditionalNGSlot('description')}

          <div
            class="link"
            aria-hidden="true"
            aria-label=" ${ifDefined(
              this.external
                ? 'Clicking on this link will open a page in a new tab'
                : null
            )}"
          >
            ${this.external
              ? html`⎋`
              : html`<tufts-icon
                  aria-hidden="true"
                  name="right-arrow"
                  library="system"
                  size="1rem"
                >
                </tufts-icon>`}
          </div>
        </div>
      </article>
    `;
  }

  headingTemplate(): TemplateResult {
    if (this.ngSlot.exist('heading')) {
      return html` ${this.url
        ? html` <div class="heading">
            <tufts-heading
              text-color="heading-color"
              clickable-card
              level-size="md"
            >
              <h4>
                <a
                  href="${this.url}"
                  target=${ifDefined(this.external ? '_blank' : undefined)}
                >
                  ${this.ngSlot.conditionalNGSlot('heading')}
                </a>
              </h4>
            </tufts-heading>
          </div>`
        : html`
            <tufts-heading text-color="heading-color" level-size="md">
              <h4>${this.ngSlot.conditionalNGSlot('heading')}</h4>
            </tufts-heading>
          `}`;
    }
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-teaser-card': TuftsTeaserCard;
  }
}
